import { useCallback, useRef } from 'react';

interface PreloaderOptions {
  delay?: number;
  idleCallback?: boolean;
}

interface PreloadItem {
  id: string;
  loader: () => Promise<any>;
  loaded: boolean;
  loading: boolean;
  promise?: Promise<any>;
}

class PreloadManager {
  private items = new Map<string, PreloadItem>();
  private loadingCount = 0;
  private maxConcurrent = 2;

  register(id: string, loader: () => Promise<any>) {
    if (!this.items.has(id)) {
      this.items.set(id, {
        id,
        loader,
        loaded: false,
        loading: false,
      });
    }
  }

  async preload(id: string, options: PreloaderOptions = {}): Promise<boolean> {
    const item = this.items.get(id);
    if (!item) {
      console.warn(`Preloader: Item '${id}' not registered`);
      return false;
    }

    // Return immediately if already loaded or loading
    if (item.loaded || item.loading) {
      return item.promise ? await item.promise.then(() => true).catch(() => false) : item.loaded;
    }

    // Check concurrent loading limit
    if (this.loadingCount >= this.maxConcurrent) {
      console.debug(`Preloader: Max concurrent loads reached, skipping '${id}'`);
      return false;
    }

    // Start loading
    item.loading = true;
    this.loadingCount++;

    const executeLoad = async () => {
      try {
        await item.loader();
        item.loaded = true;
        return true;
      } catch (error) {
        console.warn(`Preloader: Failed to preload '${id}':`, error);
        return false;
      } finally {
        item.loading = false;
        this.loadingCount--;
      }
    };

    // Handle timing options
    if (options.delay) {
      item.promise = new Promise((resolve) => {
        setTimeout(async () => {
          resolve(await executeLoad());
        }, options.delay);
      });
    } else if (options.idleCallback && 'requestIdleCallback' in window) {
      item.promise = new Promise((resolve) => {
        requestIdleCallback(async () => {
          resolve(await executeLoad());
        });
      });
    } else {
      // Use setTimeout(0) for async execution
      item.promise = new Promise((resolve) => {
        setTimeout(async () => {
          resolve(await executeLoad());
        }, 0);
      });
    }

    return item.promise;
  }

  isLoaded(id: string): boolean {
    return this.items.get(id)?.loaded ?? false;
  }

  isLoading(id: string): boolean {
    return this.items.get(id)?.loading ?? false;
  }

  getStats() {
    return {
      totalItems: this.items.size,
      loadedItems: Array.from(this.items.values()).filter(item => item.loaded).length,
      loadingItems: this.loadingCount,
    };
  }
}

// Global preload manager instance
const preloadManager = new PreloadManager();

/**
 * Hook for smart component preloading
 */
export const usePreloader = () => {
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const registerPreload = useCallback((id: string, loader: () => Promise<any>) => {
    preloadManager.register(id, loader);
  }, []);

  const preload = useCallback((id: string, options: PreloaderOptions = {}) => {
    return preloadManager.preload(id, options);
  }, []);

  const preloadOnHover = useCallback((id: string, delay: number = 300) => {
    return {
      onMouseEnter: () => {
        // Clear any existing timeout
        if (hoverTimeoutRef.current) {
          clearTimeout(hoverTimeoutRef.current);
        }

        // Start preload after delay
        hoverTimeoutRef.current = setTimeout(() => {
          preload(id, { idleCallback: true });
        }, delay);
      },
      onMouseLeave: () => {
        // Cancel preload if user leaves before delay
        if (hoverTimeoutRef.current) {
          clearTimeout(hoverTimeoutRef.current);
        }
      },
    };
  }, [preload]);

  const preloadOnVisible = useCallback((
    elementRef: React.RefObject<HTMLElement>,
    id: string,
    options: IntersectionObserverInit = {}
  ) => {
    if (!elementRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            preload(id, { idleCallback: true });
            observer.disconnect(); // Only preload once
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '50px',
        ...options,
      }
    );

    observer.observe(elementRef.current);

    return () => observer.disconnect();
  }, [preload]);

  const isLoaded = useCallback((id: string) => {
    return preloadManager.isLoaded(id);
  }, []);

  const isLoading = useCallback((id: string) => {
    return preloadManager.isLoading(id);
  }, []);

  const getStats = useCallback(() => {
    return preloadManager.getStats();
  }, []);

  return {
    registerPreload,
    preload,
    preloadOnHover,
    preloadOnVisible,
    isLoaded,
    isLoading,
    getStats,
  };
};

// Preload common heavy components
export const initializePreloaders = () => {
  // Register component preloaders
  preloadManager.register('AnalysisPanel', () => import('../components/AnalysisPanel'));
  preloadManager.register('ChartModal', () => import('../components/ChartModal'));
  preloadManager.register('AIAnalysisModal', () => import('../components/AIAnalysisModal'));
  preloadManager.register('TradingOpportunitiesTab', () => import('../components/TradingOpportunitiesTab'));

  // Register chart library preloader (using Lightweight Charts now)
  preloadManager.register('ChartLibraries', () => import('lightweight-charts'));
};

export default usePreloader;